type KeyValue = Record<string, any>
/** 
 * Converts `underscore` notation to `camel case`
 * 
 * @param {KeyValue} obj - A valid JSON object to be manipulated
 * @returns The given object in `camel case`
 */
export const toCamel = (obj: KeyValue): (KeyValue | undefined) => {
    if (!obj) return
    var camel: KeyValue = {}
    Object.keys(obj ?? {}).forEach((key) => {
        camel[key.split('_')
            .flatMap((v, i) => i <= 0 ? v : `${v.charAt(0).toUpperCase()}${v.slice(1)}`)
            .join('')] = (typeof obj[key] === 'object' && !Array.isArray(obj[key]))
                ? toCamel(obj[key])
                : obj[key]
    })
    return camel
}

/**
 * Parses the given object and creates a query parameter string from the key values.
 * Omits undefined, null, and empty strings and only parses the top object layer.
 * 
 * @param {KeyValue} data - A valid key -> value JSON object.
 * @returns {string} A url query parameter formatted string, or an empty string if there are no valid query parameters.
 */
export const createQueryParameters = <Obj extends KeyValue>(data?: Obj): string => {
    if (!data) return ''

    var values: string[] = []
    Object.keys(data ?? {}).forEach((key) => {
        if (data[key] != null && data[key] !== '') values.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    })
    return values.length > 0 ? `?${values.join('&')}` : ''
}

/**
 * Parses the given string and casts it to a key value object, where values are returned as strings.
 * 
 * ***OBJECT VALUES ARE RETURNED AS STRINGS AT THIS TIME***
 * 
 * @param {string} queryString A query string to be parsed. The initial `?` is parsed out, but key values should be seperated by `&` then `=`
 * @returns {Partial<Obj>} A key value object where all values are strings. Keys can be infered by a given type, but are not guaranteed to exist.
 * @example
 * `?key1=value1&key2=100&key3=false&key4={foo:bar,lorem:ipsum}`
 * {
 * "key1": "value1",
 * "key2": 100,
 * "key3": false,
 * "key4": "{foo:bar,lorem:ipsum}",
 * }
 */
export const parseQueryParameters = <Obj extends KeyValue>(queryString: string): Partial<Obj> => {
    if (queryString.at(0) === '?') queryString = queryString.replace('?', '')
    return queryString.split('&').reduce((prev, string) => {
        const [key, valueString] = string.split('=')
        if (!!key && !!valueString) {
            const value = decodeString(valueString)
            return {
                ...prev,
                [key]: key in prev
                    // Duplicate keys suggest an array type.
                    ? Array.isArray(prev[key]) ? [...prev[key], value] : [prev[key], value]
                    : value
            }
        } else return prev
    }, {} as Partial<Obj>)
}

const decodeString = (string: string) => {
    const value = decodeURIComponent(string)
    if (value === 'false' || value === 'true') return Boolean(value)
    else if (!!Number(value)) return Number(value)
    else return value
}